import "../styles/presentation.css";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

/* ── Animated counter hook ─────────────────────────────────────── */
function useCounter(target, duration = 1400, started = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!started) return;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);
  return value;
}

/* ── Portal screenshot mockup ──────────────────────────────────── */
function PortalScreenshot() {
  return (
    <div className="hero-mockup" aria-hidden="true">
      {/* Browser chrome */}
      <div className="mockup-chrome">
        <span className="mockup-dot" style={{ background: "#FF5F57" }} />
        <span className="mockup-dot" style={{ background: "#FEBC2E" }} />
        <span className="mockup-dot" style={{ background: "#28C840" }} />
        <span className="mockup-bar">solutions.shelf-e.com/tableros/competitividad</span>
      </div>
      {/* Real portal screenshot */}
      <div className="mockup-screenshot-wrap">
        <img
          src="images/pantallazo_competitividad_cadenas.PNG"
          alt="Shelf-e — Tablero de Competitividad entre Cadenas"
          className="mockup-screenshot"
        />
        {/* Floating badge overlay */}
        <div className="mockup-badge mockup-badge--tl">
          <span className="mockup-badge-dot" />
          Price Intelligence
        </div>
        <div className="mockup-badge mockup-badge--br">
          <span className="mockup-badge-dot mockup-badge-dot--green" />
          Live data
        </div>
      </div>
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────── */
function Presentation({ openModal, t }) {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  const products   = useCounter(1000000, 1600, inView);
  const retailers  = useCounter(25,      1000, inView);
  const categories = useCounter(500,     1400, inView);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("demo"))     openModal(t("button_4"));
    if (urlParams.has("reseller")) openModal(t("button_1"));
    if (urlParams.has("asesor"))   openModal(t("button_6"));
  }, []);

  return (
    <section
      ref={ref}
      className={`hero-section ${inView ? "hero-visible" : ""}`}
      id="Nosotros"
      aria-label="Shelf-e — Digital Shelf Analytics platform"
    >
      <div className="hero-grid-overlay" aria-hidden="true" />

      <div className="hero-inner">
        {/* ── Left col — copy ─────────────────────────────── */}
        <div className="hero-copy">

          <p className="hero-eyebrow">Digital Shelf Analytics · LATAM</p>

          {/* H1 — slogan in a single line, no <br/> */}
          <h1 className="hero-h1">
            <span className="hero-h1-light">{t("seccion1_1")}</span>
            {" "}
            <span className="hero-h1-accent">{t("seccion1_2")}</span>
          </h1>

          <p className="hero-sub">
            {t("seccion1_3")} {t("seccion1_4")}
            {t("seccion1_5")} {t("seccion1_6")}
          </p>

          {/* Stat strip */}
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-num">
                {inView ? (products >= 1000000 ? "+1M" : `+${(products / 1000).toFixed(0)}K`) : "+1M"}
              </div>
              <div className="hero-stat-label">{t("stat_products") || "productos/día"}</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <div className="hero-stat-num">{inView ? `${retailers}+` : "25+"}</div>
              <div className="hero-stat-label">{t("stat_retailers") || "retailers"}</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <div className="hero-stat-num">{inView ? `${categories}+` : "500+"}</div>
              <div className="hero-stat-label">{t("stat_categories") || "categorías"}</div>
            </div>
          </div>

          {/* CTAs */}
          <div className="hero-ctas">
            <button className="hero-btn-primary" onClick={() => openModal(t("button_4"))}>
              {t("button_4")}
            </button>
            <button className="hero-btn-secondary" onClick={() => openModal(t("button_5"))}>
              {t("button_5")}
            </button>
          </div>

          <p className="hero-footnote">
            {t("hero_footnote") || "Sin contratos anuales · Onboarding en 48h · Soporte en español"}
          </p>
        </div>

        {/* ── Right col — portal screenshot ────────────────── */}
        <div className="hero-visual">
          <PortalScreenshot />
        </div>
      </div>
    </section>
  );
}

export default Presentation;
